import { useMemo, useState } from 'react';

import { ActorTypeEnum, getFragmentData } from '@e-socna/data-layer';
import type { Id, RepresentativeTypeId, UserRoleId } from '@e-socna/data-layer';

import { useFindOneOrganizationQuery } from '../../../pages/Organization/hooks/useFindOneOrganizationQuery.hook';
import { useReferencesContext } from '../../../contexts/ReferencesContext/hooks/useReferencesContext.hook';
import { ActorCardCmp_Organization_Fragment } from '../../../components/Actors/Card/fragments/organization.fragment';
import { quotationPartyOf } from '../../../components/Actors/utils/missing-representative-types.util';
import type { SelectionRestriction } from '../../../components/ThirdParties/types/selection-restriction.type';

import { ActorFormTab } from '../enums/tab.enum';
import { ActorThirdPartySelectFormStep } from '../steps/ThirdPartySelect';
import { ActorDetailsFormStep } from '../steps/Details';
import { ActorReferencesFormStep } from '../steps/References';
import { ActorRepresentativesSelectFormStep } from '../steps/RepresentativesSelect';
import { ActorRepresentativesDetailsFormStep } from '../steps/RepresentativesDetails';
import { DetailsSchema } from '../schemas/details.schema';
import { ReferencesSchema } from '../schemas/references.schema';
import { ThirdPartySelectSchema } from '../schemas/third-party-select.schema';
import { representativesSelectSchemaOf } from '../schemas/representative-selects.schema';
import { representativesDetailsSchemaOf } from '../schemas/representatives-details.schema';
import type { ActorForm_Actor } from '../fragments/actor.fragment';
import type { RepresentationRules } from '../utils/representation-requirements.util';
import type { RepresentativeTypeRequirement } from '../utils/required-representative-types.util';
import type { RoleRequirement } from '../utils/required-roles.util';
import type { CategoryRestriction } from '../types/category-restriction.type';

import { useRepresentationReferences } from './useRepresentationReferences.hook';

interface Params {
  readonly allowsInternalQuotationReceiver: boolean;
  readonly categoryRestriction?: CategoryRestriction;
  readonly excludedIds?: readonly Id[];
  readonly initialValues: ActorForm_Actor;
  readonly isInternal: boolean;
  readonly representationRules: RepresentationRules;
  readonly requiredRoleIds: readonly string[];
  readonly selectionRestriction?: SelectionRestriction;
  readonly stepIdsSubset?: readonly ActorFormTab[];
}

export const useActorSteps = ({
  allowsInternalQuotationReceiver,
  categoryRestriction,
  excludedIds,
  initialValues,
  isInternal,
  representationRules,
  requiredRoleIds,
  selectionRestriction,
  stepIdsSubset,
}: Readonly<Params>) => {
  const [actorType, setActorType] = useState(initialValues.kind);
  const [organizationId, setOrganizationId] = useState(
    initialValues.kind === ActorTypeEnum.ActorOrganization
      ? initialValues.thirdPartyId
      : undefined,
  );

  const [hasRepresentatives, setHasRepresentatives] = useState(
    initialValues.__typename === 'ActorOrganization' &&
      !!initialValues.representatives.length,
  );

  const { data, isLoading } = useFindOneOrganizationQuery(organizationId);
  const representationReferences = useRepresentationReferences();
  const { genericStatusesCodeMap, representativeTypesIdMap, userRolesIdMap } =
    useReferencesContext();
  const enabledStatusId = genericStatusesCodeMap.Enabled._id;
  // The roles each internal organization carries: the internal list only.
  const roleRequirement = useMemo<RoleRequirement | undefined>(
    () =>
      isInternal && requiredRoleIds.length
        ? {
            requiredRoleIds,
            enabledStatusId,
            labelOf: (roleId) =>
              userRolesIdMap[roleId as UserRoleId]?.label ?? 'Inconnu',
          }
        : undefined,
    [enabledStatusId, isInternal, requiredRoleIds, userRolesIdMap],
  );
  // The representative types the quotation party of the list carries.
  const typeRequirement = useMemo<
    RepresentativeTypeRequirement | undefined
  >(() => {
    const party = quotationPartyOf(
      isInternal ? 'internal' : 'external',
      representationRules,
      representationReferences,
    );

    return party.requiredTypeIds.length
      ? {
          party,
          enabledStatusId,
          labelOf: (typeId) =>
            representativeTypesIdMap[typeId as RepresentativeTypeId]?.label ??
            'Inconnu',
        }
      : undefined;
  }, [
    enabledStatusId,
    isInternal,
    representationReferences,
    representationRules,
    representativeTypesIdMap,
  ]);

  const { steps, wizardChildren } = useMemo(() => {
    const memberships =
      getFragmentData(
        ActorCardCmp_Organization_Fragment,
        data?.findOneOrganization,
      )?.allMemberships ?? [];

    const actorsThirdPartySelect = {
      step: {
        id: ActorFormTab.ThirdPartySelect,
        title: 'Sélection Tiers',
        isEnabled: true,
        ValidationSchema: ThirdPartySelectSchema,
      },
      child: (
        <ActorThirdPartySelectFormStep
          excludedIds={excludedIds}
          id={ActorFormTab.ThirdPartySelect}
          isInternal={isInternal}
          key={ActorFormTab.ThirdPartySelect}
          selectionRestriction={selectionRestriction}
          setActorType={setActorType}
          setOrganizationId={setOrganizationId}
        />
      ),
    };

    const actorsDetails = {
      step: {
        id: ActorFormTab.ActorsDetails,
        title: 'Détails Acteur',
        isEnabled: true,
        ValidationSchema: DetailsSchema,
      },
      child: (
        <ActorDetailsFormStep
          allowsInternalQuotationReceiver={allowsInternalQuotationReceiver}
          categoryRestriction={categoryRestriction}
          id={ActorFormTab.ActorsDetails}
          isInternalList={isInternal}
          key={ActorFormTab.ActorsDetails}
        />
      ),
    };

    // The references an external actor gives the entity: the external list only.
    const actorsReferences = {
      step: {
        id: ActorFormTab.References,
        title: 'Références',
        isEnabled: !isInternal,
        ValidationSchema: ReferencesSchema,
      },
      child: (
        <ActorReferencesFormStep
          id={ActorFormTab.References}
          key={ActorFormTab.References}
        />
      ),
    };

    const actorsRepresentativesSelect = {
      step: {
        id: ActorFormTab.RepresentativesSelect,
        title: 'Sélection Représentants',
        isEnabled: actorType === ActorTypeEnum.ActorOrganization,
        ValidationSchema: representativesSelectSchemaOf(
          representationRules,
          representationReferences,
        ),
      },
      child: (
        <ActorRepresentativesSelectFormStep
          id={ActorFormTab.RepresentativesSelect}
          isInternal={isInternal}
          key={ActorFormTab.RepresentativesSelect}
          organization={data?.findOneOrganization}
          representationRules={representationRules}
          setHasRepresentatives={setHasRepresentatives}
        />
      ),
    };

    const actorsRepresentativesDetails = {
      step: {
        id: ActorFormTab.RepresentativesDetails,
        title: 'Détails Représentants',
        isEnabled:
          actorType === ActorTypeEnum.ActorOrganization && hasRepresentatives,
        ValidationSchema: representativesDetailsSchemaOf({
          memberships,
          roleRequirement,
          typeRequirement,
        }),
      },
      child: (
        <ActorRepresentativesDetailsFormStep
          id={ActorFormTab.RepresentativesDetails}
          key={ActorFormTab.RepresentativesDetails}
          organization={data?.findOneOrganization}
          roleRequirement={roleRequirement}
          typeRequirement={typeRequirement}
        />
      ),
    };

    const allTabs = [
      [ActorFormTab.ThirdPartySelect, actorsThirdPartySelect],
      [ActorFormTab.ActorsDetails, actorsDetails],
      [ActorFormTab.References, actorsReferences],
      [ActorFormTab.RepresentativesSelect, actorsRepresentativesSelect],
      [ActorFormTab.RepresentativesDetails, actorsRepresentativesDetails],
    ] as const;

    const activeTabs = stepIdsSubset?.length
      ? allTabs.filter(([tab]) => stepIdsSubset.includes(tab))
      : allTabs;

    const steps = activeTabs.map(([, { step }]) => step);
    const wizardChildren = activeTabs.map(([, { child }]) => child);

    return { steps, wizardChildren };
  }, [
    actorType,
    allowsInternalQuotationReceiver,
    categoryRestriction,
    data,
    excludedIds,
    hasRepresentatives,
    isInternal,
    representationReferences,
    representationRules,
    roleRequirement,
    selectionRestriction,
    stepIdsSubset,
    typeRequirement,
  ]);

  return { steps, wizardChildren, isLoading };
};
